import React, { useCallback } from 'react'
import { FlatList } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { Stack } from '@zet/design-system'
import { KeyBoardWrapper } from '@zet/utils'
import { useAddCustomerState } from '@zet/zet-pro-quick-journey/hooks/useAddCustomer'
import { useForm } from 'react-hook-form'

import { Button, ScreenTitle } from '../../components'
import FormController from '../../form/FormController'
import {
  sendAnalyticsData,
  sendJfsCustomEvent,
} from '../../utils/analyticsEvents'
import { getJfsCustomerId } from '../../utils/jfsSession'
import { panDetailsValidationScheme } from '../../utils/validationSchemas'
import { customerPanDetailsFormFields } from './formFields'

export const CustomerPanDetails = () => {
  const { mutate, isLoading } = useAddCustomerState()
  const { customerId } = useAddCustomerStore()

  const {
    control,
    formState: { isValid },
    getValues,
    setError,
  } = useForm({
    mode: 'onTouched',
    resolver: zodResolver(panDetailsValidationScheme),
  })

  const renderItem = useCallback(({ item }) => {
    return <FormController control={control} ocField={item} />
  }, [])

  const handleSubmit = () => {
    const custId = getJfsCustomerId()
    sendAnalyticsData({
      screen: 'SHOW_PAN_SCREEN_JFS',
      type: 'click',
    })
    sendJfsCustomEvent('enter_pancard_details', {
      customer_id: custId || customerId || '',
      click_header: 'One last thing',
      click_text: 'Continue',
    })
    const { dob, panNumber } = getValues()
    const requestBodyData = {
      dob,
      panNo: panNumber,
      customerId: custId || customerId,
      screenName: 'SHOWN_PAN_SCREEN_JFS',
    }
    mutate(requestBodyData, {
      onSuccess: (data) => {
        // Backend surfaces validation failures (e.g. "PAN already exists") as a
        // resolved response with error code 400 instead of throwing. Show it inline
        // on the PAN field so the user gets persistent feedback and knows to edit
        // the value, matching the normal customer-addition flow.
        const errorCode = data?.code ?? data?.error?.code
        if (errorCode === 400) {
          setError('panNumber', {
            type: 'server',
            message: data?.message ?? data?.error?.message,
          })
          return
        }
        sendAnalyticsData({ screen: data?.action, type: 'screen' })
      },
      onError: (error: any) => {
        // The same validation failure can also come back as an HTTP 400, which
        // rejects the request so the success handler above never runs. Surface
        // it on the PAN field either way.
        if (error?.response?.status !== 400) {
          return
        }
        const errorBody = error?.response?.data?.error ?? error?.response?.data
        if (errorBody?.message) {
          setError('panNumber', {
            type: 'server',
            message: errorBody.message,
          })
        }
      },
    })
  }

  return (
    <KeyBoardWrapper
      BottomSheet={
        <Button
          disabled={!isValid || isLoading}
          onPress={handleSubmit}
          title={'Continue'}
          loading={isLoading}
          loaderText="Verifying"
        />
      }
    >
      <Stack className="bg-white px-3">
        <FlatList
          showsVerticalScrollIndicator={false}
          data={customerPanDetailsFormFields}
          ListHeaderComponent={
            <ScreenTitle
              title={'One last thing'}
              subTitle="Please confirm your PAN and date of birth."
            />
          }
          renderItem={renderItem}
        />
      </Stack>
    </KeyBoardWrapper>
  )
}
